// import {ListControllerProps, RaRecord} from 'ra-core';
import {Form} from 'react-router-dom';
import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {DeleteButtonWithConfirmation, EditButton} from 'components/refinedev/action_buttons';
import {LiveSearchFilter} from 'components/refinedev/filter-forms/LiveSearchFilter';
import ListPage from 'pages/_shared/list-page';
// import useTable from 'hooks/refinedev/useTable';
import Pagination from 'components/refinedev/shared/Pagination';
import formatter from '@/lib/formatter';
import MediaViewPopup from './image-view-popup';
import useDialogState from 'hooks/useDialogState';
import MediaCreatePopup from './image-create-popup';
import {ImageItemView} from './components';
import {Button} from '@/shadcn_components/ui/button';
import {BaseRecord, useTable, useTableReturnType} from '@refinedev/core';
import {defaultQueryOptions} from '@/pages/_utils';
import StateBoundary from '@/components/basic/StateBoundary';
import {useMemo} from 'react';

const thisResource = 'images';

const columnHelper = createColumnHelper<any>();

const columns = [
  columnHelper.accessor('id', {
    header: () => 'ID',
    cell: info => info.getValue(),
    meta: {
      headerClassName: 'w-12',
    },
  }),
  columnHelper.accessor('extension', {
    header: () => 'Loại file',
    cell: info => info.renderValue(),
    meta: {
      headerClassName: 'w-24',
    },
  }),
  columnHelper.accessor('size', {
    header: () => 'Dung lượng',
    cell: info => info.renderValue(),
    meta: {
      headerClassName: 'w-24',
    },
  }),
  columnHelper.accessor(row => row.id, {
    id: 'actions',
    cell: info => <EditButton id={String(info.getValue())} resource={thisResource} />,
    enableSorting: false,
    header: () => <span />,
    meta: {
      headerClassName: 'w-12',
    },
  }),
];

const MediaList = () => {
  const useTableResults = useTable({
    resource: thisResource,
    sorters: {initial: [{field: 'id', order: 'asc'}]},
    queryOptions: defaultQueryOptions,      
    meta: {append: ['url', 'thumbnails']},
  });

  const dialog = useDialogState();
  function popupUpload() {
    dialog.openDialog();
  }

  return (
    <StateBoundary
      isLoading={useTableResults.tableQuery.isLoading}
      error={useTableResults.tableQuery.error}>
      <div className="flex flex-col gap-4 pt-3">
        {/* filter form */}
        <Form>
          <div className="flex flex-row items-center gap-2">
            <div className="w-56">
              <LiveSearchFilter
                filters={useTableResults.filters}
                setFilters={useTableResults.setFilters}
              />
            </div>
            <div className="flex-grow"></div>
            <Button onClick={popupUpload}>Tải lên</Button>
          </div>
        </Form>
        {/* card grid */}
        <Cardgrid columns={columns} tableResults={useTableResults}/>
      </div>
      <MediaCreatePopup open={dialog.open} closeDialog={dialog.closeDialog} />
    </StateBoundary>
  );
};

const Cardgrid = <RecordType extends BaseRecord = BaseRecord>({
  meta,
  columns,
  tableResults,
  // toggleItemSelectState,
  // isSelected,
}: // Pick<UseSelectedList, 'isSelected' | 'toggleItemSelectState'> &
{
  meta?: any;
  columns: ColumnDef<RecordType>[];
  tableResults: useTableReturnType;
}) => {
  const data = tableResults.tableQuery.data?.data || [];
  const total = tableResults.tableQuery.data?.total || 0;
  const {current, setCurrent, setSorters, pageSize, sorters} = tableResults;

  const viewPopup = useDialogState();

  function popupShowImage(media) {
    viewPopup.openDialog({src: media.url, title: media.filename});
  }

  const pagination = useMemo(
    () => ({
      pageIndex: current - 1,
      pageSize,
    }),
    [current, pageSize],
  );

  const sorting = useMemo<SortingState>(
    () => [
      {
        id: sorters?.[0]?.field,
        desc: sorters?.[0]?.order === 'desc',
      },
    ],
    [sorters],
  );

  const table = useReactTable({
    data: data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    pageCount: Math.ceil(total / pageSize),
    state: {
      pagination,
      sorting,
    },
    manualPagination: true,
    manualSorting: true,
    meta,
  });

  return (
    <div className="h-full w-full rounded-none">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
        {table.getRowModel().rows.map(row => (
          <ImageItemView
            key={row.original.id}
            onClick={() => popupShowImage(row.original)}
            src={
              row.original?.thumbnails?.thumbnail_medium ||
              row.original?.thumbnails?.thumbnail ||
              'no-image'
            }
            alt={row.original.filename}
            header={
              <p className="bg-gray-100">
                #{row.original.id}: {formatter.formatDateTime(row.original?.updated_at)}
              </p>
            }
            footer={
              <DeleteButtonWithConfirmation id={String(row.original.id)} resource={thisResource} />
            }
            // checkable={false}
          />
        ))}
      </div>
      <Pagination table={table} tableResults={tableResults} />
      <MediaViewPopup
        open={viewPopup.open}
        onClose={viewPopup.closeDialog}
        src={viewPopup.extraData?.src}
        title={viewPopup.extraData?.title}
      />
    </div>
  );
};

MediaList.table_columns = columns;

export default MediaList;
